
import {
    type ChatMessageResponse,
    type BlockContent,
    type ImagesBlockPayload,
    type MessageReference,
    type MessageButton,
    SenderType,
} from '../types/chat-message';
import type { ChatMessage, ImageUploadItem } from '../types/chat-agent';
import { ChatAgentType } from '@constants/chat-agents';
import { transformBlockContent } from './dataTransform';
import {
    getComponentChipLabel,
    type ChatComponentItem,
    type ComponentType,
} from '../types/chat-component';

const COMPONENT_TYPE_TO_ATTACHMENT: Record<ComponentType, string> = {
    order: 'order',
    product: 'product',
    ingredient: 'material',
    customer: 'customer',
    supplier: 'supplier',
    employee: 'staff',
};

const buildBlockContentsFromUI = (
    text: string,
    attachedComponent?: ChatComponentItem | null,
): BlockContent[] => {
    const blocks: BlockContent[] = [];
    let order = 0;

    if (text?.trim()) {
        blocks.push({
            type: 'TEXT',
            payload: {
                text,
                order: order++,
            },
        });
    }

    if (attachedComponent) {
        const attachmentType = COMPONENT_TYPE_TO_ATTACHMENT[attachedComponent.type];
        blocks.push({
            type: 'COMPONENT_INFO',
            payload: {
                text: getComponentChipLabel(attachedComponent),
                order: order++,
                attachmentType,
                data: { attachmentType },
            },
        });
    }

    if (blocks.length === 0) {
        blocks.push({
            type: 'TEXT',
            payload: {
                text: text || '',
                order: 0,
            },
        });
    }

    return blocks;
};
/**
 * Preprocesses raw message text before markdown rendering.
 *
 * Handles servers that send double-escaped strings, e.g.:
 *   \n  (2 chars: backslash + n)   → real newline
 *   \\n (2 backslashes + n)        → real newline
 *   \u003e (backslash + u003e)      → >
 *   \\u003e (2 backslashes + u003e)→ >
 *   &gt; / &lt; / &amp;             → > / < / &
 *
 * Applied uniformly for socket messages, history messages, and forwarded messages.
 */
export const preprocessMessageText = (text: string): string => {
    if (!text) return text;

    let result = text;

    // 1. Decode unicode escapes: handle 1 or 2 leading backslashes before uXXXX
    //    e.g., \u003e → >   and   \\u003e → >
    //    In JS regex source: \\{1,2} matches 1 or 2 literal backslashes
    result = result.replaceAll(/\\{1,2}u([0-9a-fA-F]{4})/g, (_, hex) =>
        String.fromCodePoint(Number.parseInt(hex, 16))
    );

    // 2. Convert escaped newlines: handle 1 or 2 leading backslashes before n
    //    e.g., \n (2 chars) → newline   and   \\n (3 chars) → newline
    result = result.replaceAll(/\\{1,2}n/g, '\n');

    // 3. Convert escaped tabs (less common but possible)
    result = result.replaceAll(/\\{1,2}t/g, '\t');

    // 4. Decode common HTML entities
    result = result
        .replaceAll('&amp;', '&')
        .replaceAll('&lt;', '<')
        .replaceAll('&gt;', '>')
        .replaceAll('&quot;', '"')
        .replaceAll('&#39;', "'")
        .replaceAll('&apos;', "'");

    return result;
};

/**
 * Preprocess all text fields inside blockContents.
 * This ensures blockContents raw text also gets the same treatment
 * (used when ChatAdmin renders structured blocks directly from payload).
 * Also transforms snake_case fields to camelCase.
 */
const preprocessBlockContents = (blockContents: BlockContent[] | undefined): BlockContent[] | undefined => {
    if (!blockContents?.length) return blockContents;
    return blockContents.map((block) => {
        // First, transform snake_case to camelCase
        const transformedBlock = transformBlockContent(block);

        if (typeof transformedBlock.type !== 'string' || transformedBlock.type.toUpperCase() !== 'TEXT') return transformedBlock;
        const payload = transformedBlock.payload as any;
        if (!payload?.text) return transformedBlock;
        return {
            ...transformedBlock,
            payload: {
                ...payload,
                text: preprocessMessageText(payload.text),
            },
        };
    });
};

/**
 * True for API blocks whose type is IMAGES (case-insensitive).
 */
export const isImagesBlockType = (block: { type?: string } | null | undefined): boolean =>
    typeof block?.type === 'string' && block.type.toUpperCase() === 'IMAGES';

/**
 * Parse image URLs from an IMAGES block payload (handles common API variants).
 */
export const extractImageUrlsFromImagesPayload = (payload: unknown): string[] => {
    if (payload == null || typeof payload !== 'object') return [];
    const p = payload as ImagesBlockPayload & Record<string, unknown>;
    const out: string[] = [];

    const pushStringArray = (arr: unknown) => {
        if (!Array.isArray(arr)) return;
        for (const u of arr) {
            if (typeof u === 'string' && u.trim()) out.push(u.trim());
        }
    };

    pushStringArray(p.urls);
    pushStringArray(p.imageUrls);
    pushStringArray(p.fileUrls);
    pushStringArray((p as Record<string, unknown>).links);

    const pushObjects = (arr: unknown[]) => {
        for (const item of arr) {
            if (typeof item === 'string' && item.trim()) {
                out.push(item.trim());
                continue;
            }
            if (item && typeof item === 'object') {
                const o = item as Record<string, unknown>;
                const url = o.url ?? o.src ?? o.uri ?? o.imageUrl ?? o.presignedUrl ?? o.thumbnailUrl;
                if (typeof url === 'string' && url.trim()) out.push(url.trim());
            }
        }
    };

    if (Array.isArray(p.images)) pushObjects(p.images);
    if (Array.isArray(p.items)) pushObjects(p.items);
    if (Array.isArray((p as Record<string, unknown>).medias)) pushObjects((p as Record<string, unknown>).medias as unknown[]);

    if (typeof p.imageUrl === 'string' && p.imageUrl.trim()) {
        out.push(p.imageUrl.trim());
    }

    // One-level nested payload (common in API wrappers)
    if (p.data && typeof p.data === 'object' && !Array.isArray(p.data)) {
        out.push(...extractImageUrlsFromImagesPayload(p.data));
    }

    return [...new Set(out)];
};

/** Ordered URLs from every IMAGES block in blockContents */
export const extractImageUrlsFromBlockContents = (
    blockContents: BlockContent[] | undefined | null
): string[] => {
    if (!blockContents?.length) return [];
    const urls: string[] = [];
    for (const block of blockContents) {
        if (!isImagesBlockType(block)) continue;
        urls.push(...extractImageUrlsFromImagesPayload(block.payload));
    }
    return urls;
};

/** Join TEXT payloads only — for USER bubble UI when separating IMAGES from copy */
export const extractTextFromTextBlocksOnly = (
    blockContents: BlockContent[] | undefined | null
): string => {
    if (!blockContents?.length) return '';
    const parts: string[] = [];
    for (const block of blockContents) {
        const isText = typeof block.type === 'string' && block.type.toUpperCase() === 'TEXT';
        if (!isText) continue;
        const payload = block.payload as { text?: string };
        if (payload?.text?.trim()) parts.push(preprocessMessageText(payload.text));
    }
    return parts.join('\n');
};

/**
 * Extract combined text from blockContents
 * Also extracts imageUrl from TEXT payload, and IMAGES blocks as markdown lines
 */
const extractCombinedText = (msg: ChatMessageResponse): string => {
    if (!msg.blockContents?.length) {
        return preprocessMessageText(msg.fullContent || '');
    }

    const parts: string[] = [];

    msg.blockContents.forEach((block: BlockContent) => {
        if (isImagesBlockType(block)) {
            extractImageUrlsFromImagesPayload(block.payload).forEach((url) => {
                parts.push(`![image](${url})`);
            });
            return;
        }

        const isText = typeof block.type === 'string' && block.type.toUpperCase() === 'TEXT';
        if (!isText) return;

        const payload = block.payload as any;

        // Extract text content
        if (payload?.text?.trim()) {
            parts.push(preprocessMessageText(payload.text));
        }

        // Extract imageUrl (from CSKH image uploads)
        if (payload?.imageUrl && payload?.uploadStatus === 'SUCCESS') {
            parts.push(`![image](${payload.imageUrl})`);
        }
    });

    if (parts.length === 0) {
        return preprocessMessageText(msg.fullContent || '');
    }

    return parts.join('\n');
};

/**
 * Extract references, followUpQuestions, and buttons from blockContents
 */
const extractReferencesFollowUpsAndButtons = (msg: ChatMessageResponse): {
    references: MessageReference[];
    followUpQuestions: string[];
    buttons: MessageButton[];
} => {
    const allReferences: MessageReference[] = [];
    const allFollowUpQuestions: string[] = [];
    const allButtons: MessageButton[] = [];

    if (msg.blockContents?.length) {
        msg.blockContents.forEach((block: BlockContent) => {
            const payload = block.payload as any;
            if (payload?.references) {
                allReferences.push(...payload.references);
            }
            if (payload?.followUpQuestions) {
                allFollowUpQuestions.push(...payload.followUpQuestions);
            }
            if (payload?.buttons) {
                allButtons.push(...payload.buttons);
            }
        });
    }

    // Deduplicate references
    const uniqueReferences: MessageReference[] = [];
    const referenceKeys = new Set<string>();
    allReferences.forEach((ref) => {
        const key = ref.uri || ref.title || '';
        if (key && !referenceKeys.has(key)) {
            referenceKeys.add(key);
            uniqueReferences.push(ref);
        } else if (!key) {
            uniqueReferences.push(ref);
        }
    });

    // Deduplicate follow-up questions
    const uniqueFollowUps = [...new Set(allFollowUpQuestions)];

    // Deduplicate buttons
    const uniqueButtons: MessageButton[] = [];
    const buttonKeys = new Set<string | number>();
    allButtons.forEach((btn) => {
        const key = btn.id !== undefined && btn.id !== null ? String(btn.id) : btn.name;
        if (!buttonKeys.has(key)) {
            buttonKeys.add(key);
            uniqueButtons.push(btn);
        }
    });

    return {
        references: uniqueReferences,
        followUpQuestions: uniqueFollowUps,
        buttons: uniqueButtons,
    };
};

/**
 * Align blockContents with fullContent for historical messages.
 * If the server returned incomplete blockContents (e.g. only 1-2 chunks)
 * but has the complete text in fullContent, this helper repairs blockContents.
 */
export const alignBlockContentsWithFullContent = (
    blockContents: BlockContent[] | undefined,
    fullContent: string | undefined
): BlockContent[] | undefined => {
    if (!blockContents?.length || !fullContent?.trim()) return blockContents;

    const isSpecialCard = blockContents.some(b =>
        ['ORDER_INFO', 'PRODUCT_LIST', 'REVENUE_REPORT', 'PROFIT_LOSS_REPORT', 'INCOME_EXPENSE_REPORT', 'STOCK_REPORT', 'COMPONENT_INFO'].includes(b.type)
    );
    if (isSpecialCard) return blockContents;

    // Calculate the total text length inside all TEXT blocks in blockContents
    let totalBlockTextLength = 0;
    blockContents.forEach((block) => {
        const isText = typeof block.type === 'string' && block.type.toUpperCase() === 'TEXT';
        if (isText) {
            const payload = block.payload as { text?: string };
            if (payload?.text) {
                totalBlockTextLength += payload.text.trim().length;
            }
        }
    });

    const cleanFullContent = fullContent.trim();
    // If fullContent is significantly longer than the text inside blocks
    if (cleanFullContent.length > totalBlockTextLength) {
        let hasUpdatedFirstText = false;
        const alignedBlocks: BlockContent[] = [];

        blockContents.forEach((block) => {
            const isText = typeof block.type === 'string' && block.type.toUpperCase() === 'TEXT';
            if (isText) {
                if (!hasUpdatedFirstText) {
                    // Update the first text block to hold the entire fullContent
                    alignedBlocks.push({
                        ...block,
                        payload: {
                            ...block.payload,
                            text: cleanFullContent,
                        } as any,
                    });
                    hasUpdatedFirstText = true;
                }
                // Skip subsequent text blocks to prevent duplicated/truncated text showing up
            } else {
                alignedBlocks.push(block);
            }
        });

        // If there were no text blocks at all, append a text block at the start
        if (!hasUpdatedFirstText) {
            alignedBlocks.unshift({
                type: 'TEXT',
                payload: {
                    text: cleanFullContent,
                    order: 0,
                } as any,
            });
        }

        return alignedBlocks;
    }

    return blockContents;
};

/**
 * Đăng ký message đang được "Tại đây" forward sang targetAgent (theo messageId/codeMessage).
 * convertToChatMessage sẽ ép agentType của các message này về targetAgent, bất kể typeChat
 * gốc server trả về là gì — tránh bị filter theo typeChat (chatService.ts parseHistoryMessages) loại bỏ nhầm.
 * handleForwardToAgent (useChat.ts) set khi bắt đầu forward, tự dọn sau vài giây.
 */
export const forwardOverrideRegistry: { targetAgent: ChatAgentType | null; ids: Set<string> } = {
    targetAgent: null,
    ids: new Set(),
};

/**
 * Convert ChatMessageResponse to ChatMessage
 * All API fields are preserved, UI fields are added
 * Extracts references, followUpQuestions, and buttons from blockContents to top level
 */
export const convertToChatMessage = (
    msg: ChatMessageResponse,
    agentType?: ChatAgentType,
    isHistory: boolean = false
): ChatMessage => {
    const alignedBlockContents = alignBlockContentsWithFullContent(msg.blockContents, msg.fullContent);
    const msgWithAlignedBlocks = {
        ...msg,
        blockContents: alignedBlockContents || [],
    };

    const combinedText = extractCombinedText(msgWithAlignedBlocks);
    const { references, followUpQuestions, buttons } = extractReferencesFollowUpsAndButtons(msgWithAlignedBlocks);

    // Prefer string IDs to avoid JS number precision loss
    // Socket uses messageIdStr, history uses idString
    const msgId = String(msg.messageIdStr || msg.idString || msg.id || msg.messageId);

    const isSingleResponse = typeof msg.responseMode === 'string' &&
        msg.responseMode.toLowerCase() === 'single';

    const finalButtons = (msg.buttons && msg.buttons.length > 0) ? msg.buttons : (buttons.length > 0 ? buttons : undefined);

    // Clean text and blockContents from trailing buttons suffix if buttons are present
    let cleanedText = combinedText;
    let cleanedBlockContents = alignedBlockContents;
    if (finalButtons && finalButtons.length > 0) {
        const buttonNames = finalButtons.map((btn: any) => typeof btn.name === 'string' ? btn.name.trim() : '').filter(Boolean);
        if (buttonNames.length > 0) {
            const escapeRegExp = (string: string) => {
                return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
            };
            const regexPattern = '\\s*\\??\\s*' + buttonNames.map(name => escapeRegExp(name)).join('\\s*;\\s*') + '\\s*;?\\s*$';
            const suffixRegex = new RegExp(regexPattern);

            const cleanString = (str: string): string => {
                return str.replace(suffixRegex, '').trim();
            };

            cleanedText = cleanString(combinedText);

            if (cleanedBlockContents) {
                cleanedBlockContents = cleanedBlockContents.map((block) => {
                    if (typeof block.type === 'string' && block.type.toUpperCase() === 'TEXT') {
                        const payload = block.payload as any;
                        if (payload?.text) {
                            return {
                                ...block,
                                payload: {
                                    ...payload,
                                    text: cleanString(payload.text),
                                },
                            };
                        }
                    }
                    return block;
                });
            }
        }
    }

    const chatMessage: ChatMessage = {
        ...msg,
        isFinal: isHistory ? true : (isSingleResponse ? true : (msg.isFinal !== undefined ? msg.isFinal : true)),
        id: msgId,
        messageId: msg.messageIdStr || msg.idString || msg.messageId || msgId,
        msgBotFwToNewAgentId: msg.msgBotFwToNewAgentId,
        // Preprocess blockContents so ChatAdmin block-by-block rendering also gets clean text
        blockContents: preprocessBlockContents(cleanedBlockContents) as any,
        references: (msg.references && msg.references.length > 0) ? msg.references : (references.length > 0 ? references : undefined),
        followUpQuestions: (msg.followUpQuestions && msg.followUpQuestions.length > 0) ? msg.followUpQuestions : (followUpQuestions.length > 0 ? followUpQuestions : undefined),
        buttons: finalButtons,
        text: cleanedText,
        // Also preprocess suggestAgentNotify in case it contains \n or \uXXXX
        suggestAgentNotify: msg.suggestAgentNotify
            ? preprocessMessageText(msg.suggestAgentNotify)
            : msg.suggestAgentNotify,
        user: msg.senderType,
        isRead: true,
        uploadStatus: msg.isError ? 'failed' : undefined,
        time: msg.createdAt || (msg as any).time || msg.updatedAt || new Date().toISOString(),
        // Ưu tiên typeChat thật của message (server gắn) hơn agentType truyền vào —
        // tránh việc bulk-load lịch sử theo 1 agent gán nhầm agentType cho message
        // thực chất thuộc agent khác (dẫn tới hiện thị sai tab).
        // Exception: message đang được "Tại đây" forward sang agentType (targetAgent) —
        // luôn ép về agentType dù typeChat gốc là agent cũ.
        agentType: (() => {
            const msgId = String(msg.messageIdStr || msg.idString || msg.id || msg.messageId || '');
            const msgCode = String((msg as any).codeMessage || '');
            const isForwardedToThisAgent =
                agentType &&
                forwardOverrideRegistry.targetAgent === agentType &&
                ((msgId && forwardOverrideRegistry.ids.has(msgId)) ||
                    (msgCode && forwardOverrideRegistry.ids.has(msgCode)));
            if (isForwardedToThisAgent) return agentType;
            return Object.values(ChatAgentType).includes(msg.typeChat as ChatAgentType)
                ? (msg.typeChat as ChatAgentType)
                : agentType;
        })(),
        _isHistory: isHistory,
    };

    return chatMessage;
};


export type MessageCallback = (
    chatMessage: ChatMessage
) => void;

/**
 * Create ChatMessage from UI fields only (for local messages)
 */
export const createChatMessageFromUI = (
    partial: {
        text: string;
        user: SenderType;
        agentType: ChatAgentType;
        messageId?: string;
        isRead?: boolean;
        time?: string;
        references?: MessageReference[];
        followUpQuestions?: string[];
        buttons?: any[];
        suggestAgentNotify?: string;
        intent?: string;
        audio?: string;
        audioRawText?: string;
        msgUserFwCodeMessage?: string;
        codeMessage?: string;
        uploadStatus?: 'uploading' | 'success' | 'failed';
        uploadError?: string;
        localFiles?: File[];
        imageUploadItems?: ImageUploadItem[];
        attachedComponent?: import('../types/chat-component').ChatComponentItem | null;
        quotedMessageId?: string;
        quotedMessageText?: string;
        quotedMessageUser?: SenderType;
        confirmSwitchAgent?: ChatAgentType;
        isConfirmDisabled?: boolean;
        originalBlockContents?: BlockContent[];
        blockContents?: BlockContent[]; 
    }
): ChatMessage => {
    const now = new Date().toISOString();
    const defaultBlockContents = buildBlockContentsFromUI(
        partial.text,
        partial.attachedComponent,
    );
    return {
        // API fields with defaults
        isHidden: false,
        id: partial.messageId ? String(partial.messageId) : Date.now().toString(),
        isHasReactionMessage: false,
        messageId: partial.messageId ? String(partial.messageId) : Date.now().toString(),
        isFEGeneratedId: !partial.messageId,
        conversationId: 0,
        businessId: '',
        blockContents: defaultBlockContents,
        senderType: partial.user,
        fullContent: partial.text,
        createdAt: partial.time || now,
        updatedAt: now,
        sendMessageUser: false,
        typeChat: partial.agentType,
        isSynced: false,
        isFinal: true,
        typeMessage: 'text',
        isWriteLog: false,
        // UI fields
        text: partial.text,
        user: partial.user,
        isRead: partial.isRead ?? true,
        suggestAgentNotify: partial.suggestAgentNotify,
        time: partial.time || now,
        agentType: partial.agentType,
        // Optional fields
        ...(partial.references && { references: partial.references }),
        ...(partial.followUpQuestions && { followUpQuestions: partial.followUpQuestions }),
        ...(partial.buttons && { buttons: partial.buttons }),
        // ...(partial.suggestAgentNotify && { suggestAgentNotify: partial.suggestAgentNotify }),
        ...(partial.intent && { intent: partial.intent }),
        ...(partial.audio && { audio: partial.audio }),
        ...(partial.audioRawText && { audioRawText: partial.audioRawText }),
        ...(partial.msgUserFwCodeMessage && { msgUserFwCodeMessage: partial.msgUserFwCodeMessage }),
        ...(partial.codeMessage && { codeMessage: partial.codeMessage }),
        // Upload status fields
        ...(partial.uploadStatus && { uploadStatus: partial.uploadStatus }),
        ...(partial.uploadError && { uploadError: partial.uploadError }),
        ...(partial.localFiles && { localFiles: partial.localFiles }),
        ...(partial.imageUploadItems && { imageUploadItems: partial.imageUploadItems }),
        ...(partial.attachedComponent && { attachedComponent: partial.attachedComponent }),
        // Quote/Reply fields
        ...(partial.quotedMessageId && { quotedMessageId: partial.quotedMessageId }),
        ...(partial.quotedMessageText && { quotedMessageText: partial.quotedMessageText }),
        ...(partial.quotedMessageUser && { quotedMessageUser: partial.quotedMessageUser }),
        // Confirm-switch fields
        ...(partial.confirmSwitchAgent && { confirmSwitchAgent: partial.confirmSwitchAgent }),
        ...(partial.isConfirmDisabled !== undefined && { isConfirmDisabled: partial.isConfirmDisabled }),
        ...(partial.originalBlockContents && { originalBlockContents: partial.originalBlockContents }),
        // Override blockContents if provided (for re-added OrderCard)
        ...(partial.blockContents && { blockContents: partial.blockContents }),
        _isHistory: false,
    };
};

/**
 * Process and emit message as ChatMessage
 * All API response fields are preserved in the ChatMessage object
 */
export const processAndEmitMessage = (
    msg: ChatMessageResponse,
    onMessage: MessageCallback,
    agentType?: ChatAgentType,
    isHistory: boolean = false
): void => {
    const chatMessage = convertToChatMessage(msg, agentType, isHistory);
    onMessage(chatMessage);
};

export const isSimilarId = (
    id1: string | number | undefined | null,
    id2: string | number | undefined | null
): boolean => {
    if (id1 === undefined || id1 === null || id2 === undefined || id2 === null) return false;
    const str1 = String(id1).trim();
    const str2 = String(id2).trim();
    if (str1 === str2) return true;

    const num1 = Number(str1);
    const num2 = Number(str2);
    if (!isNaN(num1) && !isNaN(num2) && num1 > 1e10 && num2 > 1e10) {
        return Math.abs(num1 - num2) < 1000;
    }
    return false;
};


